// Ported from design/project/App24p-Profile.dc.html, then wired: the player's own level, record, streaks, winnings,
// stake limit and rivals, with the ways to Invite friends and Wallet. Badges arrive in a later step.
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { useGame } from "../game.js";

interface Profile {
  level: number;
  xp: number;
  levelFrom: number;
  levelTo: number;
  weekRank: number | null;
  wins: number;
  losses: number;
  draws: number;
  streak: number;
  bestStreak: number;
  wonTotal: string;
  stakeLimit: number;
  battles: number;
  longPct: number | null;
  rivals: { id: string; name: string; rounds: number; wins: number; losses: number }[];
}

const MONO = { fontFamily: "'JetBrains Mono', monospace", fontWeight: "800" } as const;
const COLOURS = ["#FF3366", "#8A7CFF", "#2AABEE"];
const lead = (a: number, b: number) => (a > b ? "#CCFF00" : a < b ? "#FF3366" : "#FFFFFF");

export function App24_Profile() {
  const { me } = useGame();
  const [p, setP] = useState<Profile | null>(null);
  useEffect(() => {
    void api<Profile>("/v1/profile").then(setP).catch(() => {});
  }, []);
  const name = me ? (me.user.username ? `@${me.user.username}` : me.user.firstName) : "...";
  const initials = name.replace(/^@/, "").slice(0, 2).toUpperCase();
  const played = p ? p.wins + p.losses + p.draws : 0;
  const rate = played ? Math.round((p!.wins / played) * 100) : 0;
  const ring = 2 * Math.PI * 46;
  const pct = p ? Math.min(100, ((p.xp - p.levelFrom) / (p.levelTo - p.levelFrom)) * 100) : 0;
  const nextLimit = !p ? "" : p.stakeLimit >= 1000 ? "the top limit"
    : p.stakeLimit >= 250 ? [p.battles < 100 && `${100 - p.battles} battles`, p.level < 10 && "LV 10"].filter(Boolean).join(" and ") + " to 1K"
    : `${Math.max(0, 20 - p.battles)} battles to 250`;
  return (
    <>
      <style>{`@media (max-height:900px){.rivalMore2{display:none}}@media (max-height:760px){.rivalMore1,.rivalMore2{display:none}}`}</style>
      <div className="screen">
  <div className="orbL" aria-hidden="true" style={{ top: "-100px", left: "-45px", width: "480px", height: "480px" }}></div>
  <div className="pad squeeze" style={{ height: "100%", boxSizing: "border-box", paddingBottom: "calc(var(--dd-nav-bottom) + 76px)", display: "flex", flexDirection: "column" }}>
    <div className="top" style={{ justifyContent: "center" }}><span className="syne" style={{ fontWeight: "800", fontSize: "17px" }}>Profile</span></div>

    <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "16px" }}>
      <div style={{ width: "84px", height: "84px", borderRadius: "50%", padding: "3px", flex: "none", boxSizing: "border-box", background: "conic-gradient(from 200deg, #CCFF00, #3DFFD0, #8A7CFF, #FF3366, #CCFF00)" }}>
        <div className="av" style={{ width: "78px", height: "78px", background: "#CCFF00", fontSize: "24px", border: "3px solid #0B0E14", boxSizing: "border-box" }}>{initials}</div>
      </div>
      <div style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column", gap: "6px" }}>
        <span className="syne" style={{ fontWeight: "800", fontSize: "22px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
        <div style={{ display: "flex", gap: "6px" }}>
          <span className="chip chipL" style={{ height: "22px", fontSize: "10px" }}>WEEK #{p?.weekRank ?? "–"}</span>
          <span className="chip chipN" style={{ height: "22px", fontSize: "10px" }}>LV {p?.level ?? me?.level ?? 1}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ flex: "1", maxWidth: "120px", height: "6px", borderRadius: "3px", background: "#30363D", overflow: "hidden", display: "block" }}><span style={{ display: "block", width: `${pct}%`, height: "100%", background: "#CCFF00" }}></span></span>
          <span className="mono muted" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>{p ? `${p.xp.toLocaleString("en-US")} / ${p.levelTo.toLocaleString("en-US")} XP` : "..."}</span>
        </div>
      </div>
    </div>

    <div style={{ flexShrink: 0, marginTop: "16px", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
      <div className="g card" style={{ gridRow: "span 2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", padding: "12px" }}>
        <svg width="104" height="104" viewBox="0 0 110 110" role="img" aria-label={`Win rate ${rate} percent`}>
          <circle cx="55" cy="55" r="46" fill="none" stroke="#30363D" strokeWidth="10"></circle>
          <circle cx="55" cy="55" r="46" fill="none" stroke="#CCFF00" strokeWidth="10" strokeLinecap="round" strokeDasharray={`${(rate / 100) * ring} ${ring}`} transform="rotate(-90 55 55)"></circle>
          <text x="55" y="60" textAnchor="middle" fontFamily="JetBrains Mono" fontWeight="800" fontSize="22" fill="#FFFFFF">{played ? `${rate}%` : "–"}</text>
        </svg>
        <span className="lbl">WIN RATE</span>
        <span className="mono muted" style={{ fontSize: "12px" }}>{p ? `${p.wins} W · ${p.losses} L · ${p.draws} D` : "..."}</span>
      </div>
      <div className="g card" style={{ padding: "12px 14px" }}>
        <span className="lbl" style={{ fontSize: "10px" }}>BEST STREAK</span>
        <div className="mono" style={{ fontWeight: "800", fontSize: "22px", marginTop: "2px" }}>{p?.bestStreak ?? "–"}</div>
        <span className="muted" style={{ fontSize: "10px" }}>{p && p.streak >= 2 ? `now x${p.streak} 🔥` : "wins in a row"}</span>
      </div>
      <div className="g card" style={{ padding: "12px 14px" }}>
        <span className="lbl" style={{ fontSize: "10px" }}>WON IN TOTAL</span>
        <div className="mono lime" style={{ fontWeight: "800", fontSize: "20px", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis" }}>{p?.wonTotal ?? "–"}</div>
      </div>
      <div className="g card" style={{ padding: "12px 14px" }}>
        <span className="lbl" style={{ fontSize: "10px" }}>STAKE LIMIT</span>
        <div className="mono" style={{ fontWeight: "800", fontSize: "20px", marginTop: "2px" }}>{p ? p.stakeLimit.toLocaleString("en-US") : "–"}</div>
        <span className="muted" style={{ fontSize: "10px" }}>{nextLimit}</span>
      </div>
      <div className="g card" style={{ padding: "12px 14px" }}>
        <span className="lbl" style={{ fontSize: "10px" }}>LONG / SHORT</span>
        <div className="mono" style={{ fontWeight: "800", fontSize: "20px", marginTop: "2px" }}>{p?.longPct === null || !p ? "–" : <><span className="lime">{p.longPct}</span><span className="muted">/</span><span className="red">{100 - p.longPct}</span></>}</div>
      </div>
    </div>

    <div className="row" style={{ flexShrink: 0, marginTop: "14px" }}><span className="lbl">RIVALS</span>{p && p.rivals.length > 0 && <span className="muted" style={{ fontSize: "12px" }}>you · them</span>}</div>
    <div className="g" style={{ flexShrink: 0, marginTop: "8px", borderRadius: "18px", padding: "2px 0" }}>
      {p && p.rivals.length === 0 && <div className="li"><span className="muted" style={{ fontSize: "13px" }}>Your rivals show up here once you've battled.</span></div>}
      {p?.rivals.map((r, i) => (
        <div key={r.id} className={i > 0 ? `rivalMore${i}` : undefined}>
          {i > 0 && <div className="div" style={{ margin: "0 14px" }}></div>}
          <div className="li">
            <div className="av" style={{ width: "32px", height: "32px", background: COLOURS[i % COLOURS.length], fontSize: "10px", flex: "none" }}>{r.name.replace(/^@/, "").slice(0, 2).toUpperCase()}</div>
            <div style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: "14px", fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
              <span className="muted" style={{ fontSize: "11px" }}>{r.rounds} round{r.rounds === 1 ? "" : "s"}</span>
            </div>
            <span style={{ ...MONO, fontSize: "15px", color: lead(r.wins, r.losses) }}>{r.wins}</span>
            <span style={{ ...MONO, fontSize: "13px", color: "#4B535E", margin: "0 4px" }}>–</span>
            <span style={{ ...MONO, fontSize: "15px" }}>{r.losses}</span>
          </div>
        </div>
      ))}
    </div>

    <div style={{ flexShrink: 0, marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
      <a href="#/App27" className="g li" style={{ borderRadius: "18px", color: "#FFFFFF", textDecoration: "none", minWidth: "0" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2" strokeLinecap="round" style={{ flex: "none" }} aria-hidden="true"><circle cx="9" cy="8" r="3.5"></circle><path d="M2.5 20c1-3.5 3.5-5.5 6.5-5.5s5.5 2 6.5 5.5"></path><path d="M18 8v6M15 11h6"></path></svg>
        <span style={{ flex: "1", minWidth: "0", fontSize: "14px", fontWeight: "600" }}>Invite friends<span className="lime" style={{ display: "block", fontSize: "11px", fontWeight: "600" }}>earn 20%</span></span>
      </a>
      <a href="#/App28" className="g li" style={{ borderRadius: "18px", color: "#FFFFFF", textDecoration: "none", minWidth: "0" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2" strokeLinecap="round" style={{ flex: "none" }} aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="3"></rect><path d="M16 13h2"></path><path d="M3 10h18"></path></svg>
        <span style={{ flex: "1", minWidth: "0", fontSize: "14px", fontWeight: "600" }}>Wallet<span className="mono muted" style={{ display: "block", fontSize: "11px", fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{me?.balance.available ?? "..."} USDT</span></span>
      </a>
    </div>
  </div>

  <nav className="nav g" aria-label="Main">
    <a href="#/App09"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 17.5 L3 6V3h3l11.5 11.5"></path><path d="M13 19l6-6M16 16l4 4M19 21l2-2"></path></svg>Arena</a>
    <a href="#/App23"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z"></path><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3"></path></svg>Ranks</a>
    <a href="#/App25"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>History</a>
    <a href="#/App24" className="on"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="8" r="4"></circle><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"></path></svg>Profile</a>
  </nav>
</div>
    </>
  );
}
